import type { FileTypeDefinition } from "./experience";
import type { CardCategoryDocument, CardTemplateDocument } from "./product";
import type { WorldDocument } from "./types";

/** Five defaults; creators may explicitly show additional types without deleting schemas. */
export function projectCategoryNavigation(categories: readonly CardCategoryDocument[], audience: "creator" | "player" = "creator") {
  const allowed = categories.filter(category => category.enabled && (audience === "creator" || category.visibleToPlayers)).sort((a, b) => a.order - b.order);
  const defaults = new Set(allowed.slice(0, 5).map(category => category.id));
  const isShown = (category: CardCategoryDocument) => category.navigationVisible ?? defaults.has(category.id);
  return { primary: allowed.filter(isShown), additional: allowed.filter(category => !isShown(category)) };
}

export interface WorldFileGroup { category: CardCategoryDocument; documents: WorldDocument[] }

/** Exact file type identity takes precedence over broad kinds such as item or character. */
export function worldFileGroups(documents: readonly WorldDocument[], categories: readonly CardCategoryDocument[], templates: readonly CardTemplateDocument[]): WorldFileGroup[] {
  const allowed = categories.filter(category => category.enabled && category.visibleToPlayers);
  const groups = new Map(allowed.map(category => [category.id, { category, documents: [] as WorldDocument[] }]));
  const aliases = new Map(templates.map(template => [template.categoryId, template.designation.replaceAll("-", "_").toLowerCase()]));
  for (const document of documents) {
    if (document.deletedAt || ["gm", "creator", "hidden"].includes(document.displayVisibility ?? "")) continue;
    const exact = categories.find(category => category.id === document.fileTypeId);
    if (exact && (!exact.enabled || !exact.visibleToPlayers)) continue;
    const kind = (document.fileTypeId?.startsWith("core.") ? document.fileTypeId.slice(5) : document.kind).replaceAll("-", "_").toLowerCase();
    const category = exact ?? (!document.fileTypeId || document.fileTypeId.startsWith("core.") ? allowed.find(item => aliases.get(item.id) === kind) : undefined);
    const id = category?.id ?? document.fileTypeId ?? `kind:${document.kind}`;
    if (!groups.has(id)) groups.set(id, { category: { id, name: document.kind, description: "", templateId: "", order: groups.size, enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "◇", updatedAt: 0 }, documents: [] });
    groups.get(id)!.documents.push(document);
  }
  return [...groups.values()];
}

export interface FileTypeNavigation { primaryIds: string[] }
export interface FileTypeNavigationItem { id: string; name: string; designation: string }
export interface FileTypeNavigationView { primary: FileTypeNavigationItem[]; additional: FileTypeNavigationItem[] }

/** Navigation is a projection. It never removes definitions from agent discovery. */
export function projectFileTypeNavigation(
  definitions: readonly FileTypeDefinition[], experienceId: string,
  navigation: FileTypeNavigation, audience: "player" | "gm" | "creator",
): FileTypeNavigationView {
  const allowed = definitions.filter(type => type.experienceId === experienceId && (
    type.displayVisibility === "public" || type.displayVisibility === "player" || audience === "creator" || audience === "gm" && type.displayVisibility === "gm"
  ));
  const canonical = new Map(allowed.map(type => [type.id, type]));
  const primaryIds = [...new Set(navigation.primaryIds)].filter(id => canonical.has(id)).slice(0, 5);
  const item = (type: FileTypeDefinition): FileTypeNavigationItem => ({ id: type.id, name: type.name, designation: type.designation });
  return { primary: primaryIds.map(id => item(canonical.get(id)!)), additional: allowed.filter(type => !primaryIds.includes(type.id)).map(item) };
}

/** Showing an existing type is not an instruction to create a second schema. */
export function addPrimaryFileType(navigation: FileTypeNavigation, definition: FileTypeDefinition, experienceId: string): FileTypeNavigation {
  if (definition.experienceId !== experienceId) throw new Error("File type belongs to another experience");
  const primaryIds = [...new Set(navigation.primaryIds)];
  if (primaryIds.includes(definition.id)) return { primaryIds };
  if (primaryIds.length >= 5) throw new Error("Choose a primary type to replace before adding another");
  return { primaryIds: [...primaryIds, definition.id] };
}
